/**
 * OutlinePendingView — Interactive outline review for supervised generation.
 *
 * Features:
 * - Inline editing of headings, themes, key points
 * - Add/remove/reorder sections
 * - Per-section and full outline regeneration
 * - Feedback as regeneration input or content generation guidance
 */

import { useState, useCallback } from 'react';
import {
  Check,
  Edit3,
  RefreshCw,
  Loader2,
  ArrowLeft,
  FileText,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  X,
  MessageSquare,
  ArrowUp,
  ArrowDown,
  Target,
  BarChart3,
  Link2,
  Lightbulb,
  BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useOutlineEdit,
  useOutlineRegenerate,
  useSectionOutlineRegenerate,
  useOutlineFeedback,
  useStrategyArtifact,
} from '@/hooks/useSupervisedGeneration';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface OutlinePendingViewProps {
  review: any;
  contentId: string;
  onApprove: (userSelections?: any) => void;
  onCancel: () => void;
  isApprovePending: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ZONE_COLORS: Record<string, string> = {
  educational: 'bg-teal-light text-teal-dark border-teal/20',
  brand_cta: 'bg-coral-light text-coral-dark border-coral/20',
  trust: 'bg-teal-light text-teal-dark border-teal/30',
  conversion: 'bg-coral-light text-coral-dark border-coral/30',
};

const ZONE_ACCENTS: Record<string, string> = {
  educational: 'border-l-teal',
  brand_cta: 'border-l-coral',
  trust: 'border-l-teal',
  conversion: 'border-l-coral',
};

// ---------------------------------------------------------------------------
// Strategy Artifact Panel — renders full strategy MD from DB
// ---------------------------------------------------------------------------

function StrategyModal({
  contentId,
  open,
  onClose,
}: {
  contentId: string;
  open: boolean;
  onClose: () => void;
}) {
  const { data, isLoading } = useStrategyArtifact(open ? contentId : null);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-card rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-coral" />
            <h3 className="text-base font-semibold">Full Strategy</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted/50">
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* Modal body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading strategy...
            </div>
          ) : data?.content_md ? (
            <div className="prose prose-sm prose-slate max-w-none
              [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mb-3
              [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mb-2 [&_h2]:mt-5
              [&_h3]:text-sm [&_h3]:font-medium [&_h3]:mb-1.5
              [&_p]:text-sm [&_p]:mb-2 [&_p]:leading-relaxed
              [&_table]:text-xs [&_th]:px-3 [&_th]:py-1.5 [&_td]:px-3 [&_td]:py-1
              [&_table]:border-collapse [&_th]:border [&_td]:border [&_th]:bg-muted/50
              [&_ul]:text-sm [&_li]:mb-1
              [&_strong]:font-semibold
              [&_hr]:my-4
            ">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.content_md}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-8 text-center">No strategy artifact available yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OutlinePendingView({
  review,
  contentId,
  onApprove,
  onCancel,
  isApprovePending,
}: OutlinePendingViewProps) {
  // --- Data extraction ---
  const sectionSpecs: any[] = review?.section_specs || [];
  const rawStrategy = review?.raw_strategy || null;
  const strategyOutlines: Record<string, any> = rawStrategy?.section_outlines || {};
  const articleTitle = review?.title || '';
  const metaDescription = review?.meta_description || '';
  const totalWords = sectionSpecs.reduce((sum: number, s: any) => sum + (s.target_words || 0), 0);

  // H3 extraction from raw_strategy.headings
  const h3sBySectionIndex: Record<number, string[]> = {};
  if (rawStrategy?.headings) {
    for (const [blockId, headingText] of Object.entries(rawStrategy.headings)) {
      const m = blockId.match(/(?:auto_)?h3_(?:\w+_)?(\d+)/);
      if (m) {
        const idx = parseInt(m[1], 10);
        if (!h3sBySectionIndex[idx]) h3sBySectionIndex[idx] = [];
        h3sBySectionIndex[idx].push(headingText as string);
      }
    }
  }

  // Template structure summary
  const templateMeta = review?.template_meta || {};
  const introBlockTypes: any[] = templateMeta.intro_block_types || [];
  const allBlockTypes = [...introBlockTypes, ...sectionSpecs.flatMap((s: any) => s.block_types || [])];
  const templateImageCount = allBlockTypes.filter((b: any) => b.block_type === 'ai-image' || b.block_type === 'image').length;
  const templateHasFaq = allBlockTypes.some((b: any) => b.block_type === 'faq' || b.block_type === 'faqSchema');
  const templateHasStatsGrid = allBlockTypes.some((b: any) => b.block_type === 'statsGrid');
  const templateHasTable = allBlockTypes.some((b: any) => b.block_type === 'table');
  const templateHasCallout = allBlockTypes.some((b: any) => b.block_type === 'callout');
  const hasAnyTemplate = templateImageCount > 0 || templateHasFaq || templateHasStatsGrid || templateHasTable || templateHasCallout;

  // --- Edit state ---
  const [editMode, setEditMode] = useState(false);
  const [editedSpecs, setEditedSpecs] = useState<any[]>([]);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedMeta, setEditedMeta] = useState('');

  // --- Feedback state ---
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');

  // --- Per-section regen state ---
  const [regenIdx, setRegenIdx] = useState<number | null>(null);
  const [regenFeedback, setRegenFeedback] = useState('');

  // --- Template structure collapse ---
  const [templateExpanded, setTemplateExpanded] = useState(false);

  // --- Strategy tabs ---
  const [strategyTab, setStrategyTab] = useState<'themes' | 'seo' | 'links' | 'media' | 'blocks'>('themes');

  // --- Strategy modal ---
  const [showStrategyModal, setShowStrategyModal] = useState(false);

  // --- Interactive selections (user curates what the strategist suggested) ---

  // Media selections: per-section, which media types to use
  // Initialize from strategy media_plan with checked=true, plus unchecked alternatives
  const MEDIA_TYPES = ['stock_photo', 'ai_generated', 'youtube_embed'] as const;
  const MEDIA_LABELS: Record<string, string> = {
    stock_photo: 'Stock Photo (Pexels/Unsplash)',
    ai_generated: 'AI Generated (fal.ai)',
    youtube_embed: 'YouTube Video',
  };

  const buildInitialMediaSelections = () => {
    const selections: Record<string, Array<{ type: string; checked: boolean; search_query: string; placement: string; alt_text: string }>> = {};
    // Initialize from strategy media_plan
    for (const item of (rawStrategy?.media_plan || [])) {
      const sid = item.section_id;
      if (!selections[sid]) selections[sid] = [];
      selections[sid].push({
        type: item.media_type,
        checked: true,  // Strategist's recommendation = pre-checked
        search_query: item.search_query || '',
        placement: item.placement || 'after_first_paragraph',
        alt_text: item.alt_text || '',
      });
    }
    // Add unchecked alternatives for sections that have a recommendation
    for (const spec of sectionSpecs) {
      const sid = spec.section_id;
      if (!selections[sid]) selections[sid] = [];
      const existingTypes = new Set(selections[sid].map((s: any) => s.type));
      for (const mt of MEDIA_TYPES) {
        if (!existingTypes.has(mt)) {
          selections[sid].push({
            type: mt,
            checked: false,
            search_query: '',
            placement: 'after_first_paragraph',
            alt_text: '',
          });
        }
      }
    }
    return selections;
  };
  const [mediaSelections, setMediaSelections] = useState(buildInitialMediaSelections);

  const toggleMedia = (sectionId: string, type: string) => {
    setMediaSelections(prev => {
      const items = [...(prev[sectionId] || [])];
      const idx = items.findIndex(i => i.type === type);
      if (idx >= 0) items[idx] = { ...items[idx], checked: !items[idx].checked };
      return { ...prev, [sectionId]: items };
    });
  };

  const updateMediaQuery = (sectionId: string, type: string, query: string) => {
    setMediaSelections(prev => {
      const items = [...(prev[sectionId] || [])];
      const idx = items.findIndex(i => i.type === type);
      if (idx >= 0) items[idx] = { ...items[idx], search_query: query };
      return { ...prev, [sectionId]: items };
    });
  };

  // Link selections: research topics as checkable items + custom URLs
  const buildInitialLinkSelections = () => {
    const topics: Array<{ topic: string; section_id: string; checked: boolean }> = [];
    for (const sel of (rawStrategy?.section_external_links || [])) {
      for (const topic of (sel.research_topics || [])) {
        topics.push({ topic, section_id: sel.section_id, checked: sel.priority <= 2 });
      }
    }
    return topics;
  };
  const [linkSelections, setLinkSelections] = useState(buildInitialLinkSelections);
  const [customLinks, setCustomLinks] = useState<Array<{ url: string; label: string }>>([]);

  const toggleLink = (idx: number) => {
    setLinkSelections(prev => prev.map((l, i) => i === idx ? { ...l, checked: !l.checked } : l));
  };

  const addCustomLink = () => {
    setCustomLinks(prev => [...prev, { url: '', label: '' }]);
  };

  const updateCustomLink = (idx: number, field: 'url' | 'label', value: string) => {
    setCustomLinks(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const removeCustomLink = (idx: number) => {
    setCustomLinks(prev => prev.filter((_, i) => i !== idx));
  };

  // Block selections: which special blocks to include
  const buildInitialBlockSelections = () => {
    const blocks: Array<{ block_id: string; block_type: string; checked: boolean; content: any }> = [];
    for (const block of (rawStrategy?.structured_block_assignments || [])) {
      blocks.push({
        block_id: block.block_id,
        block_type: block.block_type,
        checked: true,  // Strategist's recommendation = pre-checked
        content: {
          assigned_questions: block.assigned_questions || [],
          assigned_topics: block.assigned_topics || [],
          assigned_focus: block.assigned_focus || '',
          assigned_subject: block.assigned_subject || '',
        },
      });
    }
    // Add common blocks if not already present
    const existingTypes = new Set(blocks.map(b => b.block_type));
    if (!existingTypes.has('key_takeaways')) {
      blocks.unshift({ block_id: 'key_takeaways_auto', block_type: 'key_takeaways', checked: true, content: { assigned_focus: 'Summarize the most important takeaways' } });
    }
    if (!existingTypes.has('faq') && !existingTypes.has('faqSchema')) {
      blocks.push({ block_id: 'faq_auto', block_type: 'faq', checked: true, content: { assigned_questions: [] } });
    }
    return blocks;
  };
  const [blockSelections, setBlockSelections] = useState(buildInitialBlockSelections);

  const toggleBlock = (idx: number) => {
    setBlockSelections(prev => prev.map((b, i) => i === idx ? { ...b, checked: !b.checked } : b));
  };

  const updateBlockQuestion = (blockIdx: number, qIdx: number, value: string) => {
    setBlockSelections(prev => prev.map((b, i) => {
      if (i !== blockIdx) return b;
      const qs = [...(b.content.assigned_questions || [])];
      qs[qIdx] = value;
      return { ...b, content: { ...b.content, assigned_questions: qs } };
    }));
  };

  const addBlockQuestion = (blockIdx: number) => {
    setBlockSelections(prev => prev.map((b, i) => {
      if (i !== blockIdx) return b;
      return { ...b, content: { ...b.content, assigned_questions: [...(b.content.assigned_questions || []), ''] } };
    }));
  };

  const removeBlockQuestion = (blockIdx: number, qIdx: number) => {
    setBlockSelections(prev => prev.map((b, i) => {
      if (i !== blockIdx) return b;
      return { ...b, content: { ...b.content, assigned_questions: b.content.assigned_questions.filter((_: any, j: number) => j !== qIdx) } };
    }));
  };

  const updateBlockTopic = (blockIdx: number, tIdx: number, value: string) => {
    setBlockSelections(prev => prev.map((b, i) => {
      if (i !== blockIdx) return b;
      const ts = [...(b.content.assigned_topics || [])];
      ts[tIdx] = value;
      return { ...b, content: { ...b.content, assigned_topics: ts } };
    }));
  };

  const addBlockTopic = (blockIdx: number) => {
    setBlockSelections(prev => prev.map((b, i) => {
      if (i !== blockIdx) return b;
      return { ...b, content: { ...b.content, assigned_topics: [...(b.content.assigned_topics || []), ''] } };
    }));
  };

  const removeBlockTopic = (blockIdx: number, tIdx: number) => {
    setBlockSelections(prev => prev.map((b, i) => {
      if (i !== blockIdx) return b;
      return { ...b, content: { ...b.content, assigned_topics: b.content.assigned_topics.filter((_: any, j: number) => j !== tIdx) } };
    }));
  };

  const updateBlockFocus = (blockIdx: number, value: string) => {
    setBlockSelections(prev => prev.map((b, i) => i === blockIdx ? { ...b, content: { ...b.content, assigned_focus: value } } : b));
  };

  // --- Theme selections (editable) ---
  const [themeSelections, setThemeSelections] = useState<string[]>(
    () => rawStrategy?.key_themes || []
  );

  // --- SEO approach (editable) ---
  const [seoApproach, setSeoApproach] = useState(() => rawStrategy?.seo_approach || '');
  const [linkingPlan, setLinkingPlan] = useState(() => rawStrategy?.linking_plan || '');
  const updateTheme = (idx: number, value: string) => {
    setThemeSelections(prev => prev.map((t, i) => i === idx ? value : t));
  };
  const removeTheme = (idx: number) => {
    setThemeSelections(prev => prev.filter((_, i) => i !== idx));
  };
  const addTheme = () => {
    setThemeSelections(prev => [...prev, '']);
  };

  // --- Mutations ---
  const outlineEditMutation = useOutlineEdit();
  const outlineRegenMutation = useOutlineRegenerate();
  const sectionRegenMutation = useSectionOutlineRegenerate();
  const feedbackMutation = useOutlineFeedback();

  // --- Handlers ---
  const enterEditMode = useCallback(() => {
    setEditedSpecs(sectionSpecs.map((s: any) => ({
      section_id: s.section_id,
      heading: s.heading || '',
      theme: (strategyOutlines[s.section_id]?.theme || s.theme || '').replace(/^Cover the topic of:\s*/i, ''),
      key_points: [...(strategyOutlines[s.section_id]?.key_points?.length > 0
        ? strategyOutlines[s.section_id].key_points
        : s.key_points || [])],
      target_words: s.target_words || 200,
      zone: s.zone || 'educational',
    })));
    setEditedTitle(articleTitle);
    // Use full strategy_summary for editing — the stored meta_description may be truncated
    const fullSummary = rawStrategy?.strategy_summary || '';
    setEditedMeta(fullSummary || metaDescription);
    setEditMode(true);
  }, [sectionSpecs, strategyOutlines, articleTitle, metaDescription, rawStrategy]);

  const saveEdits = useCallback(() => {
    outlineEditMutation.mutate(
      { contentId, payload: { sections: editedSpecs, title: editedTitle, meta_description: editedMeta } },
      {
        onSuccess: () => { toast.success('Outline updated'); setEditMode(false); },
        onError: (err) => { toast.error(`Failed to save: ${(err as any)?.response?.data?.detail || err.message}`); },
      },
    );
  }, [contentId, editedSpecs, editedTitle, editedMeta, outlineEditMutation]);

  const handleFullRegenerate = useCallback(() => {
    outlineRegenMutation.mutate(
      { contentId, payload: { feedback: feedbackText || undefined } },
      {
        onSuccess: () => { toast.success('Regenerating outline...'); setShowFeedback(false); setFeedbackText(''); },
        onError: (err) => { toast.error(`Failed: ${(err as any)?.response?.data?.detail || err.message}`); },
      },
    );
  }, [contentId, feedbackText, outlineRegenMutation]);

  const handleSaveFeedback = useCallback(() => {
    if (!feedbackText.trim()) return;
    feedbackMutation.mutate(
      { contentId, payload: { feedback: feedbackText } },
      {
        onSuccess: () => { toast.success('Feedback saved as guidance'); setShowFeedback(false); setFeedbackText(''); },
        onError: (err) => { toast.error(`Failed: ${(err as any)?.response?.data?.detail || err.message}`); },
      },
    );
  }, [contentId, feedbackText, feedbackMutation]);

  const handleSectionRegenerate = useCallback((idx: number) => {
    sectionRegenMutation.mutate(
      { contentId, payload: { section_index: idx, feedback: regenFeedback || undefined } },
      {
        onSuccess: () => { toast.success(`Section ${idx + 1} regenerated`); setRegenIdx(null); setRegenFeedback(''); },
        onError: (err) => { toast.error(`Failed: ${(err as any)?.response?.data?.detail || err.message}`); },
      },
    );
  }, [contentId, regenFeedback, sectionRegenMutation]);

  // Edit helpers
  const updateSpec = (idx: number, field: string, value: any) => {
    setEditedSpecs(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };
  const updateKeyPoint = (sIdx: number, kpIdx: number, value: string) => {
    setEditedSpecs(prev => prev.map((s, i) => {
      if (i !== sIdx) return s;
      const kps = [...s.key_points];
      kps[kpIdx] = value;
      return { ...s, key_points: kps };
    }));
  };
  const addKeyPoint = (sIdx: number) => {
    setEditedSpecs(prev => prev.map((s, i) =>
      i === sIdx ? { ...s, key_points: [...s.key_points, ''] } : s
    ));
  };
  const removeKeyPoint = (sIdx: number, kpIdx: number) => {
    setEditedSpecs(prev => prev.map((s, i) => {
      if (i !== sIdx) return s;
      return { ...s, key_points: s.key_points.filter((_: any, j: number) => j !== kpIdx) };
    }));
  };
  const moveSection = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= editedSpecs.length) return;
    setEditedSpecs(prev => {
      const arr = [...prev];
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };
  const removeSection = (idx: number) => {
    setEditedSpecs(prev => prev.filter((_, i) => i !== idx));
  };
  const addSection = () => {
    setEditedSpecs(prev => [...prev, {
      section_id: `section_${prev.length}`,
      heading: 'New Section',
      theme: '',
      key_points: [''],
      target_words: 200,
      zone: 'educational',
    }]);
  };

  // Which specs to render
  const displaySpecs = editMode ? editedSpecs : sectionSpecs;

  // --- Render ---
  const currentTotalWords = (editMode ? editedSpecs : sectionSpecs).reduce((s: number, sp: any) => s + (sp.target_words || 0), 0);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6 bg-warm-bg min-h-screen">
      {/* Strategy Modal */}
      <StrategyModal contentId={contentId} open={showStrategyModal} onClose={() => setShowStrategyModal(false)} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold tracking-tight">Review Outline</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {editMode ? 'Editing outline -- save or cancel when done' : 'Approve the outline to start section-by-section generation'}
            </p>
          </div>
        </div>
      </div>

      {/* ================================================================= */}
      {/* Strategy Overview Card                                            */}
      {/* ================================================================= */}
      <div className="bg-card border rounded-xl shadow-sm overflow-hidden">
        {/* Title & meta */}
        <div className="px-6 py-5 space-y-3">
          {editMode ? (
            <>
              <input
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                className="w-full text-xl font-bold border-b-2 border-dashed pb-1.5 outline-none focus:border-coral transition-colors"
                placeholder="Article title"
              />
              <textarea
                value={editedMeta}
                onChange={(e) => setEditedMeta(e.target.value)}
                className="w-full text-sm text-muted-foreground border rounded-lg p-3 outline-none focus:border-coral resize-none"
                rows={2}
                placeholder="Meta description / strategy summary"
              />
            </>
          ) : (
            <>
              {articleTitle && <h2 className="text-xl font-bold tracking-tight">{articleTitle}</h2>}
              {(rawStrategy?.strategy_summary || metaDescription) && (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {rawStrategy?.strategy_summary || metaDescription}
                </p>
              )}
            </>
          )}

          {/* Word count bar + stats */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <BarChart3 className="h-3.5 w-3.5 text-coral" />
              <span>{currentTotalWords.toLocaleString()} words</span>
              <span className="text-muted-foreground/40">|</span>
              <span>{displaySpecs.length} sections</span>
            </div>
            <div className="flex-1 max-w-xs">
              <div className="h-1.5 rounded-full bg-muted/50 overflow-hidden">
                <div
                  className="h-full rounded-full bg-coral transition-all duration-500"
                  style={{ width: `${Math.min(100, (currentTotalWords / Math.max(currentTotalWords, 1)) * 100)}%` }}
                />
              </div>
            </div>
            {!editMode && (
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={enterEditMode}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Edit3 className="h-3 w-3" />
                  Edit
                </button>
                <button
                  onClick={() => setShowStrategyModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-coral border border-coral/20 rounded-lg hover:bg-coral-light transition-colors"
                >
                  <BookOpen className="h-3 w-3" />
                  Full Strategy
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Strategy tabs (read-only mode only) */}
        {rawStrategy && !editMode && (
          <div className="border-t">
            {/* Tab buttons */}
            <div className="flex border-b px-6 overflow-x-auto">
              {[
                { key: 'themes' as const, label: 'Themes', icon: Lightbulb },
                { key: 'seo' as const, label: 'SEO', icon: Target },
                { key: 'links' as const, label: 'Links', icon: Link2 },
                { key: 'media' as const, label: 'Media', icon: FileText },
                { key: 'blocks' as const, label: 'Blocks', icon: BookOpen },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setStrategyTab(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors -mb-px ${
                    strategyTab === tab.key
                      ? 'border-coral text-coral'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <tab.icon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="px-6 py-4">
              {strategyTab === 'themes' && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Themes guide the tone and focus across all sections. Edit, add, or remove.</p>
                  {themeSelections.map((t: string, i: number) => (
                    <div key={i} className="flex items-center gap-2">
                      <Lightbulb className="h-3.5 w-3.5 text-teal-dark shrink-0" />
                      <input
                        value={t}
                        onChange={(e) => updateTheme(i, e.target.value)}
                        className="flex-1 text-xs border-b border-dashed outline-none focus:border-coral py-1 bg-transparent"
                        placeholder="Theme..."
                      />
                      <button onClick={() => removeTheme(i)} className="text-muted-foreground/50 hover:text-destructive">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  <button onClick={addTheme} className="flex items-center gap-1 text-[11px] text-teal hover:text-teal-dark mt-1">
                    <Plus className="h-3 w-3" /> Add Theme
                  </button>
                </div>
              )}

              {strategyTab === 'seo' && (
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">SEO Approach</p>
                    <textarea
                      value={seoApproach}
                      onChange={(e) => setSeoApproach(e.target.value)}
                      className="w-full text-sm border rounded-lg p-2.5 outline-none focus:border-coral resize-none bg-transparent leading-relaxed"
                      rows={3}
                      placeholder="Describe keyword placement strategy..."
                    />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Linking Plan</p>
                    <textarea
                      value={linkingPlan}
                      onChange={(e) => setLinkingPlan(e.target.value)}
                      className="w-full text-sm border rounded-lg p-2.5 outline-none focus:border-coral resize-none bg-transparent leading-relaxed"
                      rows={3}
                      placeholder="Describe internal/external linking strategy..."
                    />
                  </div>
                </div>
              )}

              {strategyTab === 'links' && (
                <div className="space-y-4">
                  {/* Internal links note */}
                  <div className="bg-teal-light border border-teal/20 rounded-lg px-3 py-2.5">
                    <p className="text-xs text-teal-dark">
                      Internal links will be assigned automatically from your indexed pages when generation starts.
                    </p>
                  </div>

                  {/* Research topics with checkboxes */}
                  {linkSelections.length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-foreground mb-1">External Link Research</p>
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Research Topics — select which to search</p>
                      <div className="border rounded-lg divide-y overflow-hidden">
                        {linkSelections.map((link, i) => (
                          <label key={i} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/20 ${link.checked ? 'bg-card' : 'bg-muted/10 opacity-60'}`}>
                            <input
                              type="checkbox"
                              checked={link.checked}
                              onChange={() => toggleLink(i)}
                              className="accent-coral"
                            />
                            <span className="text-[10px] font-mono text-muted-foreground shrink-0">{link.section_id}</span>
                            <span className="text-xs flex-1">{link.topic}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Custom URLs */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-muted-foreground">Custom Links — add your own URLs</p>
                      <button onClick={addCustomLink} className="flex items-center gap-1 text-[11px] text-coral hover:text-coral-hover">
                        <Plus className="h-3 w-3" /> Add Link
                      </button>
                    </div>
                    {customLinks.length > 0 && (
                      <div className="space-y-2">
                        {customLinks.map((link, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input
                              value={link.url}
                              onChange={(e) => updateCustomLink(i, 'url', e.target.value)}
                              placeholder="https://..."
                              className="flex-1 text-xs border rounded px-2 py-1.5 outline-none focus:border-coral"
                            />
                            <input
                              value={link.label}
                              onChange={(e) => updateCustomLink(i, 'label', e.target.value)}
                              placeholder="Label (e.g. authority)"
                              className="w-32 text-xs border rounded px-2 py-1.5 outline-none focus:border-coral"
                            />
                            <button onClick={() => removeCustomLink(i)} className="text-muted-foreground hover:text-destructive">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {customLinks.length === 0 && (
                      <p className="text-[10px] text-muted-foreground italic">No custom links added. Click "Add Link" to include specific URLs.</p>
                    )}
                  </div>
                </div>
              )}

              {strategyTab === 'media' && (
                <div className="space-y-4">
                  <p className="text-xs text-muted-foreground">Select which media to fetch for each section. Edit search queries to refine results.</p>
                  {displaySpecs.map((spec: any) => {
                    const sid = spec.section_id;
                    const items = mediaSelections[sid] || [];
                    if (items.length === 0) return null;
                    return (
                      <div key={sid} className="border rounded-lg overflow-hidden">
                        <div className="bg-muted/30 px-3 py-2 text-xs font-semibold text-muted-foreground">
                          {spec.heading || sid}
                        </div>
                        <div className="divide-y">
                          {items.map((item: any) => (
                            <div key={item.type} className={`flex items-start gap-3 px-3 py-2.5 ${item.checked ? 'bg-card' : 'bg-muted/10 opacity-60'}`}>
                              <input
                                type="checkbox"
                                checked={item.checked}
                                onChange={() => toggleMedia(sid, item.type)}
                                className="mt-1 accent-coral"
                              />
                              <div className={`shrink-0 w-8 h-8 rounded flex items-center justify-center text-sm ${
                                item.type === 'youtube_embed' ? 'bg-coral-light text-coral-dark' :
                                item.type === 'ai_generated' ? 'bg-teal-light text-teal-dark' :
                                'bg-teal-light text-teal-dark'
                              }`}>
                                {item.type === 'youtube_embed' ? '▶' : item.type === 'ai_generated' ? '🎨' : '📷'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium">{MEDIA_LABELS[item.type] || item.type}</p>
                                {item.checked && (
                                  <input
                                    value={item.search_query}
                                    onChange={(e) => updateMediaQuery(sid, item.type, e.target.value)}
                                    placeholder={`Search query for ${MEDIA_LABELS[item.type]}...`}
                                    className="w-full mt-1 text-xs border-b border-dashed outline-none focus:border-coral py-0.5 bg-transparent"
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {displaySpecs.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No sections to assign media to.</p>
                  )}
                </div>
              )}

              {strategyTab === 'blocks' && (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">Toggle blocks to include. Edit questions, topics, and focus text inline.</p>
                  {blockSelections.map((block, i) => {
                    const typeConfig: Record<string, { icon: string; label: string; color: string }> = {
                      faq: { icon: '❓', label: 'FAQ', color: 'bg-coral-light text-coral-dark' },
                      faqSchema: { icon: '❓', label: 'FAQ Schema', color: 'bg-coral-light text-coral-dark' },
                      bulletList: { icon: '•', label: 'Bullet List', color: 'bg-teal-light text-teal-dark' },
                      orderedList: { icon: '1.', label: 'Ordered List', color: 'bg-teal-light text-teal-dark' },
                      callout: { icon: '💡', label: 'Pro Tip', color: 'bg-coral-light text-coral-dark' },
                      key_takeaways: { icon: '✓', label: 'Key Takeaways', color: 'bg-teal-light text-teal-dark' },
                      table: { icon: '⊞', label: 'Table', color: 'bg-muted/50 text-muted-foreground' },
                    };
                    const cfg = typeConfig[block.block_type] || { icon: '◆', label: block.block_type, color: 'bg-muted/50 text-muted-foreground' };

                    return (
                      <div key={i} className={`rounded-lg border overflow-hidden ${block.checked ? 'bg-card' : 'bg-muted/10 opacity-60'}`}>
                        <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/20">
                          <input
                            type="checkbox"
                            checked={block.checked}
                            onChange={() => toggleBlock(i)}
                            className="accent-coral"
                          />
                          <span className="text-sm">{cfg.icon}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cfg.color}`}>{cfg.label}</span>
                          <span className="text-[10px] font-mono text-muted-foreground ml-auto">{block.block_id}</span>
                        </label>

                        {block.checked && (
                          <div className="px-3 pb-3 border-t">
                            {/* FAQ questions — editable */}
                            {(block.block_type === 'faq' || block.block_type === 'faqSchema') && (
                              <div className="mt-2 space-y-1.5">
                                <p className="text-[10px] font-medium text-muted-foreground">Questions:</p>
                                {(block.content.assigned_questions || []).map((q: string, j: number) => (
                                  <div key={j} className="flex items-center gap-1.5">
                                    <span className="text-coral text-xs font-medium shrink-0">Q{j + 1}:</span>
                                    <input
                                      value={q}
                                      onChange={(e) => updateBlockQuestion(i, j, e.target.value)}
                                      className="flex-1 text-xs border-b border-dashed outline-none focus:border-coral py-0.5 bg-transparent"
                                      placeholder="FAQ question..."
                                    />
                                    <button onClick={() => removeBlockQuestion(i, j)} className="text-muted-foreground/50 hover:text-destructive">
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                                <button onClick={() => addBlockQuestion(i)} className="flex items-center gap-1 text-[11px] text-coral hover:text-coral-hover">
                                  <Plus className="h-3 w-3" /> Add Question
                                </button>
                              </div>
                            )}

                            {/* List topics — editable */}
                            {(block.block_type === 'bulletList' || block.block_type === 'orderedList') && (
                              <div className="mt-2 space-y-1.5">
                                <p className="text-[10px] font-medium text-muted-foreground">Topics:</p>
                                {(block.content.assigned_topics || []).map((t: string, j: number) => (
                                  <div key={j} className="flex items-center gap-1.5">
                                    <span className="text-teal-dark text-xs shrink-0">•</span>
                                    <input
                                      value={t}
                                      onChange={(e) => updateBlockTopic(i, j, e.target.value)}
                                      className="flex-1 text-xs border-b border-dashed outline-none focus:border-coral py-0.5 bg-transparent"
                                      placeholder="List topic..."
                                    />
                                    <button onClick={() => removeBlockTopic(i, j)} className="text-muted-foreground/50 hover:text-destructive">
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                                <button onClick={() => addBlockTopic(i)} className="flex items-center gap-1 text-[11px] text-teal hover:text-teal-hover">
                                  <Plus className="h-3 w-3" /> Add Topic
                                </button>
                              </div>
                            )}

                            {/* Focus — editable */}
                            {(block.block_type === 'key_takeaways' || block.block_type === 'callout') && (
                              <div className="mt-2">
                                <p className="text-[10px] font-medium text-muted-foreground mb-1">Focus:</p>
                                <input
                                  value={block.content.assigned_focus || ''}
                                  onChange={(e) => updateBlockFocus(i, e.target.value)}
                                  className="w-full text-xs border-b border-dashed outline-none focus:border-coral py-0.5 bg-transparent"
                                  placeholder="What should this block focus on?"
                                />
                              </div>
                            )}

                            {/* Subject (images) — read only since moved to Media tab */}
                            {block.content.assigned_subject && (
                              <p className="mt-2 text-xs text-muted-foreground">Subject: <span className="font-medium">{block.content.assigned_subject}</span></p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Template structure (inline, collapsible) */}
        {hasAnyTemplate && !editMode && (
          <div className="border-t">
            <button
              onClick={() => setTemplateExpanded(!templateExpanded)}
              className="w-full flex items-center justify-between px-6 py-3 text-left hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">Template Structure</span>
                <span className="text-[10px] text-muted-foreground/60">-- auto-injected blocks</span>
              </div>
              <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${templateExpanded ? 'rotate-180' : ''}`} />
            </button>
            {templateExpanded && (
              <div className="px-6 pb-4 flex flex-wrap gap-2">
                {templateImageCount > 0 && (
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-teal-light text-teal-dark border border-teal/20">
                    Images: {templateImageCount}
                  </span>
                )}
                {templateHasFaq && (
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-teal-light text-teal-dark border border-teal/20">FAQ</span>
                )}
                {templateHasStatsGrid && (
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-teal-light text-teal-dark border border-teal/20">Stats Grid</span>
                )}
                {templateHasTable && (
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-teal-light text-teal-dark border border-teal/20">Table</span>
                )}
                {templateHasCallout && (
                  <span className="text-[11px] px-2.5 py-1 rounded-full bg-coral-light text-coral-dark border border-coral/20">Callouts</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ================================================================= */}
      {/* Section Cards                                                     */}
      {/* ================================================================= */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground px-1">
          Sections ({displaySpecs.length})
        </h3>

        {displaySpecs.map((spec: any, i: number) => {
          // Use spec.key_points as primary source -- strategy outlines may have index mismatch
          const rawKeyPoints: string[] = spec.key_points || [];
          const headingLower = (spec.heading || '').toLowerCase().trim();
          const allHeadingsLower = new Set(
            displaySpecs.map((s: any) => (s.heading || '').toLowerCase().trim()).filter(Boolean)
          );
          const keyPoints = editMode ? rawKeyPoints : rawKeyPoints.filter((kp: string) => {
            const cleaned = kp.trim().toLowerCase();
            if (!cleaned) return false;
            if (allHeadingsLower.has(cleaned)) return false;
            for (const h of allHeadingsLower) {
              if (h.length > 10 && (cleaned.startsWith(h) || h.startsWith(cleaned))) return false;
            }
            if (/^section\s+\d+$/i.test(cleaned)) return false;
            if (cleaned.startsWith('cover the topic of')) return false;
            return true;
          });
          const sectionTheme = (spec.theme || '').replace(/^Cover the topic of:\s*/i, '');
          const themeLower = sectionTheme.toLowerCase().trim();
          let cleanTheme = sectionTheme;
          if (!themeLower || /^section\s+\d+$/i.test(themeLower)) {
            cleanTheme = '';
          } else {
            for (const h of allHeadingsLower) {
              if (h.length > 10 && (themeLower.startsWith(h) || h.startsWith(themeLower) || themeLower === h)) {
                cleanTheme = '';
                break;
              }
            }
          }
          const zone = spec.zone || '';
          const zoneClass = ZONE_COLORS[zone] || 'bg-muted/50 text-muted-foreground border-muted';
          const accentClass = ZONE_ACCENTS[zone] || 'border-l-teal';
          const h3s = (h3sBySectionIndex[i] || []).filter(
            (t: string) => t.toLowerCase() !== spec.heading?.toLowerCase()
          );
          const originalSpec = sectionSpecs[i] || {};
          const blockTypes: any[] = originalSpec.block_types || [];
          const wordPct = currentTotalWords > 0 ? ((spec.target_words || 0) / currentTotalWords) * 100 : 0;

          return (
            <div
              key={spec.section_id || i}
              className={`bg-card border rounded-xl shadow-sm overflow-hidden border-l-4 ${editMode ? 'border-l-muted' : accentClass}`}
            >
              <div className="p-4 space-y-3">
                {/* Header row */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    {/* Reorder controls (edit mode) */}
                    {editMode && (
                      <div className="flex flex-col gap-0.5 mt-1">
                        <button onClick={() => moveSection(i, -1)} disabled={i === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                          <ArrowUp className="h-3 w-3" />
                        </button>
                        <button onClick={() => moveSection(i, 1)} disabled={i === displaySpecs.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30">
                          <ArrowDown className="h-3 w-3" />
                        </button>
                      </div>
                    )}

                    {/* Section number circle */}
                    <div className="w-7 h-7 rounded-full bg-coral/10 text-coral flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                      {i + 1}
                    </div>

                    {/* Title + theme */}
                    <div className="flex-1 min-w-0">
                      {editMode ? (
                        <input
                          value={spec.heading}
                          onChange={(e) => updateSpec(i, 'heading', e.target.value)}
                          className="w-full font-semibold text-sm border-b-2 border-dashed outline-none focus:border-coral pb-0.5 transition-colors"
                        />
                      ) : (
                        <p className="font-semibold text-sm">
                          {spec.needs_heading_generation
                            ? <span className="italic text-muted-foreground">{spec.heading} <span className="text-[10px] font-normal">(auto-generated)</span></span>
                            : spec.heading}
                        </p>
                      )}
                      {editMode ? (
                        <input
                          value={spec.theme}
                          onChange={(e) => updateSpec(i, 'theme', e.target.value)}
                          className="w-full text-xs text-muted-foreground mt-1 border-b border-dashed outline-none focus:border-coral transition-colors"
                          placeholder="Section theme..."
                        />
                      ) : (
                        cleanTheme && (
                          <p className="text-xs text-muted-foreground mt-0.5">{cleanTheme}</p>
                        )
                      )}
                    </div>
                  </div>

                  {/* Right side: zone badge + actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {zone && !editMode && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${zoneClass}`}>
                        {zone.replace('_', ' ')}
                      </span>
                    )}
                    {editMode ? (
                      <>
                        <input
                          type="number"
                          value={spec.target_words}
                          onChange={(e) => updateSpec(i, 'target_words', parseInt(e.target.value) || 100)}
                          className="w-16 text-[10px] text-center border rounded-md px-1.5 py-1"
                        />
                        <button onClick={() => removeSection(i)} className="text-destructive/60 hover:text-destructive transition-colors">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setRegenIdx(regenIdx === i ? null : i)}
                        className="text-muted-foreground hover:text-coral transition-colors p-1 rounded-md hover:bg-coral-light"
                        title="Regenerate this section"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Word budget bar */}
                {!editMode && (
                  <div className="flex items-center gap-2 ml-10">
                    <div className="flex-1 max-w-[200px]">
                      <div className="h-1 rounded-full bg-muted/50 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-teal/60 transition-all duration-300"
                          style={{ width: `${Math.min(100, wordPct * 2)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-[10px] text-muted-foreground font-mono tabular-nums">
                      {spec.target_words}w
                    </span>
                  </div>
                )}

                {/* H3 subsections */}
                {!editMode && h3s.length > 0 && (
                  <div className="ml-10 space-y-1.5 pt-1">
                    {h3s.map((h3Text: string, j: number) => (
                      <div key={j} className="flex items-center gap-2 pl-3 border-l-2 border-teal/20">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-teal-light text-teal-dark shrink-0 font-medium">H3</span>
                        <span className="text-xs text-muted-foreground">{h3Text}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Key points */}
                {editMode ? (
                  <div className="ml-10 space-y-1.5">
                    <p className="text-[11px] text-muted-foreground font-medium">Key points:</p>
                    {spec.key_points.map((kp: string, k: number) => (
                      <div key={k} className="flex items-center gap-1.5">
                        <Check className="h-3 w-3 text-teal/40 shrink-0" />
                        <input
                          value={kp}
                          onChange={(e) => updateKeyPoint(i, k, e.target.value)}
                          className="flex-1 text-[11px] border-b border-dashed outline-none focus:border-coral transition-colors"
                          placeholder="Key point..."
                        />
                        <button onClick={() => removeKeyPoint(i, k)} className="text-muted-foreground/50 hover:text-destructive transition-colors">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    <button onClick={() => addKeyPoint(i)} className="flex items-center gap-1 text-[11px] text-teal hover:text-teal-dark transition-colors">
                      <Plus className="h-3 w-3" /> Add point
                    </button>
                  </div>
                ) : (
                  <>
                    {keyPoints.length > 0 ? (
                      <div className="ml-10">
                        <ul className="text-xs text-muted-foreground space-y-1">
                          {keyPoints.slice(0, 5).map((kp: string, k: number) => (
                            <li key={k} className="flex items-start gap-2">
                              <Check className="h-3 w-3 text-teal shrink-0 mt-0.5" />
                              <span>{kp}</span>
                            </li>
                          ))}
                          {keyPoints.length > 5 && (
                            <li className="text-muted-foreground/50 ml-5">+{keyPoints.length - 5} more</li>
                          )}
                        </ul>
                      </div>
                    ) : (
                      <div className="ml-10 flex items-center gap-2">
                        <p className="text-[11px] text-coral italic">
                          No detailed key points generated
                        </p>
                        <button
                          onClick={() => { setRegenIdx(i); setRegenFeedback('Generate specific, actionable key points for this section'); }}
                          className="text-[11px] text-coral hover:text-coral-hover underline transition-colors"
                        >
                          Regenerate
                        </button>
                      </div>
                    )}

                    {/* Block type badges */}
                    {blockTypes.length > 0 && (() => {
                      const SUPPORTED_BLOCKS = new Set(['paragraph', 'bulletList', 'orderedList', 'faq', 'faqSchema', 'callout', 'cta']);
                      const supported = blockTypes.filter((bt: any) => SUPPORTED_BLOCKS.has(bt.block_type));
                      if (supported.length === 0) return null;
                      const blockIcons: Record<string, { icon: typeof FileText; label: string }> = {
                        paragraph: { icon: FileText, label: 'Paragraph' },
                        bulletList: { icon: Lightbulb, label: 'List' },
                        orderedList: { icon: BarChart3, label: 'Ordered List' },
                        faq: { icon: MessageSquare, label: 'FAQ' },
                        faqSchema: { icon: MessageSquare, label: 'FAQ' },
                        callout: { icon: Lightbulb, label: 'Callout' },
                        cta: { icon: Target, label: 'CTA' },
                      };
                      return (
                        <div className="ml-10 flex flex-wrap gap-1.5 pt-1">
                          {supported.map((bt: any, j: number) => {
                            const blockInfo = blockIcons[bt.block_type] || { icon: FileText, label: bt.block_type };
                            const BlockIcon = blockInfo.icon;
                            return (
                              <span key={j} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-md bg-muted/50 text-muted-foreground border">
                                <BlockIcon className="h-2.5 w-2.5" />
                                {blockInfo.label}
                              </span>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </>
                )}

                {/* Per-section regenerate popup */}
                {!editMode && regenIdx === i && (
                  <div className="ml-10 bg-warm-bg rounded-lg p-3 space-y-2 border border-coral/10">
                    <textarea
                      value={regenFeedback}
                      onChange={(e) => setRegenFeedback(e.target.value)}
                      className="w-full text-xs border rounded-lg p-2.5 outline-none focus:border-coral resize-none transition-colors"
                      rows={2}
                      placeholder="Optional: What should change about this section?"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSectionRegenerate(i)}
                        disabled={sectionRegenMutation.isPending}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-coral text-white rounded-lg hover:bg-coral-hover disabled:opacity-50 transition-colors"
                      >
                        {sectionRegenMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                        Regenerate
                      </button>
                      <button
                        onClick={() => { setRegenIdx(null); setRegenFeedback(''); }}
                        className="px-3 py-1.5 text-xs border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Add section button (edit mode) */}
        {editMode && (
          <button
            onClick={addSection}
            className="w-full py-3.5 border-2 border-dashed rounded-xl text-sm text-muted-foreground hover:text-teal hover:border-teal/40 flex items-center justify-center gap-2 transition-colors"
          >
            <Plus className="h-4 w-4" /> Add Section
          </button>
        )}
      </div>

      {/* ================================================================= */}
      {/* Feedback Panel                                                    */}
      {/* ================================================================= */}
      {!editMode && (
        <div>
          {!showFeedback ? (
            <button
              onClick={() => setShowFeedback(true)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <MessageSquare className="h-4 w-4" /> Give feedback or regenerate...
            </button>
          ) : (
            <div className="bg-card border rounded-xl shadow-sm p-5 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold">Outline Feedback</p>
                <button onClick={() => { setShowFeedback(false); setFeedbackText(''); }} className="text-muted-foreground hover:text-foreground transition-colors">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <textarea
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                className="w-full border rounded-lg p-3 text-sm outline-none focus:border-coral resize-none transition-colors"
                rows={3}
                placeholder="e.g. 'Add a section about costs' or 'Make the outline more focused on DIY steps'"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleFullRegenerate}
                  disabled={outlineRegenMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-coral text-white rounded-lg hover:bg-coral-hover disabled:opacity-50 transition-colors"
                >
                  {outlineRegenMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Regenerate Outline
                </button>
                <button
                  onClick={handleSaveFeedback}
                  disabled={!feedbackText.trim() || feedbackMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm border rounded-lg hover:bg-muted/50 disabled:opacity-50 transition-colors"
                >
                  {feedbackMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  Save as Guidance
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* Action Bar (sticky bottom)                                        */}
      {/* ================================================================= */}
      <div className="sticky bottom-0 bg-warm-bg/95 backdrop-blur-sm border-t py-4 -mx-4 px-4 flex items-center justify-end gap-3">
        {editMode ? (
          <>
            <button
              onClick={() => setEditMode(false)}
              className="px-4 py-2.5 text-sm border rounded-lg hover:bg-muted/50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveEdits}
              disabled={outlineEditMutation.isPending}
              className="px-6 py-2.5 text-sm bg-coral text-white rounded-lg hover:bg-coral-hover disabled:opacity-50 flex items-center gap-2 font-medium transition-colors shadow-sm"
            >
              {outlineEditMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save Changes
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setShowFeedback(true)}
              className="px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <MessageSquare className="h-4 w-4 inline mr-1.5" />
              Feedback
            </button>
            <button
              onClick={handleFullRegenerate}
              disabled={outlineRegenMutation.isPending}
              className="px-4 py-2.5 text-sm border rounded-lg hover:bg-muted/50 disabled:opacity-50 flex items-center gap-2 transition-colors"
            >
              {outlineRegenMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Regenerate
            </button>
            <button onClick={onCancel} className="px-4 py-2.5 text-sm border rounded-lg hover:bg-muted/50 transition-colors">
              Cancel
            </button>
            <button
              onClick={() => onApprove({
                themes: themeSelections.filter(t => t.trim()),
                seo_approach: seoApproach,
                linking_plan: linkingPlan,
                media: mediaSelections,
                links: { topics: linkSelections.filter(l => l.checked), custom_urls: customLinks.filter(l => l.url.trim()) },
                blocks: blockSelections.filter(b => b.checked),
              })}
              disabled={isApprovePending}
              className="px-8 py-2.5 text-sm bg-coral text-white rounded-lg hover:bg-coral-hover disabled:opacity-50 flex items-center gap-2 font-medium transition-colors shadow-sm"
            >
              {isApprovePending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Approve Outline
            </button>
          </>
        )}
      </div>
    </div>
  );
}
